import { useQuery } from "@tanstack/react-query";

export type ReleasePlatform = "windows" | "macos" | "linux" | "unknown";

export interface ReleaseAsset {
  id: number;
  name: string;
  size: number;
  updatedAt: string;
  platform: ReleasePlatform;
  contentType: string;
  downloadUrl: string;
}

export interface ReleaseSummary {
  id: number;
  name: string;
  tagName: string;
  publishedAt: string;
  excerpt: string;
  prerelease: boolean;
  assets: ReleaseAsset[];
}

async function fetchReleases() {
  const response = await fetch("/api/releases");

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || "Failed to fetch release catalog.");
  }

  const payload = (await response.json()) as { releases: ReleaseSummary[] };
  return payload.releases;
}

export function useReleaseCatalog() {
  return useQuery({
    queryKey: ["release-catalog"],
    queryFn: fetchReleases,
  });
}

export function useLatestRelease() {
  return useQuery({
    queryKey: ["latest-release"],
    queryFn: fetchReleases,
    select: (releases) => releases[0] ?? null,
  });
}

// ── Bug reports ─────────────────────────────────────────────────────

export interface BugReportPayload {
  title: string;
  description: string;
  category: string;
  severity: string;
  gameVersion?: string;
  platform?: string;
  gpuInfo?: string;
  modelInfo?: string;
  reporterName?: string;
  reporterContact?: string;
  sessionLog?: string | null;
  screenshot?: string | null;
  turnstileToken: string;
}

export async function submitBugReport(payload: BugReportPayload) {
  const response = await fetch("/api/bugs", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as {
      error?: string;
    } | null;
    throw new Error(data?.error ?? "Failed to submit bug report.");
  }

  return (await response.json()) as { id: number; message: string };
}
